/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React from 'react';
import {
  ExclamationTriangleIcon,
  XMarkIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

interface GPUUnavailableModalProps {
  isOpen: boolean;
  onClose: () => void;
  onJoinWaitlist: () => void;
  gpuType: string;
  containerName?: string;
  isJoining?: boolean;
}

const GPUUnavailableModal: React.FC<GPUUnavailableModalProps> = ({
  isOpen,
  onClose,
  onJoinWaitlist,
  gpuType,
  containerName,
  isJoining = false,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-gray-900 border border-gray-800 rounded-xl shadow-xl max-w-md w-full p-6 transform transition-all">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-300"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>

          {/* Icon */}
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center">
              <ExclamationTriangleIcon className="w-8 h-8 text-amber-500" />
            </div>
          </div>

          {/* Title */}
          <h3 className="text-xl font-semibold text-white text-center mb-2">
            GPU Unavailable
          </h3>

          {/* Message */}
          <p className="text-gray-400 text-center mb-4">
            {containerName ? (
              <>
                The <span className="font-medium">{gpuType}</span> GPU required for{' '}
                <span className="font-medium">{containerName}</span> is currently
                allocated to another pod.
              </>
            ) : (
              <>
                The <span className="font-medium">{gpuType}</span> GPU is currently
                unavailable. All resources are allocated to other pods.
              </>
            )}
          </p>

          {/* Info box */}
          <div className="bg-primary-500/20 border border-primary-500/30 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <ClockIcon className="w-5 h-5 text-primary-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-gray-300">
                <p className="font-medium mb-1 text-primary-500">Join the Waitlist</p>
                <p className="text-gray-400">
                  We'll notify you via email as soon as {gpuType} GPU resources
                  become available. You'll be first in line to start your pod.
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-600 text-gray-300 rounded-lg font-medium hover:bg-gray-800 transition-colors"
              disabled={isJoining}
            >
              Cancel
            </button>
            <button
              onClick={onJoinWaitlist}
              disabled={isJoining}
              className="flex-1 px-4 py-2.5 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isJoining ? (
                <>
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Joining...
                </>
              ) : (
                <>
                  <ClockIcon className="w-5 h-5" />
                  Join Waitlist
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GPUUnavailableModal;
